// @flow
import { createTarget, editTarget, getTarget, getTargets } from 'actions/trident-campaigns';
import { StatusLabel } from 'components/custom';
import { customValidate } from 'components/form/CustomValidators';
import { DEFAULT_PAGE_SIZE } from 'config';
import { CampaignStatus, countries, nodesOccMapData, tridentUserVsUCDUser } from 'data/trident-campaigns';
import { nodeTypes } from 'data/trident-types';
import _ from 'lodash';
import moment from 'moment';
import { tridentCampaigns as tridentLinkData } from 'omega-rtc/links';
import { hasTridentPermission } from 'omega-rtc/utils/user-utils';
import { EditPage } from 'omega-rtc/views/common/EditPage';
import { NewPage } from 'omega-rtc/views/common/NewPage';
import React from 'react';
import { Link } from 'react-router';
import { Popup } from 'semantic-grab-ui/src';
import type { ModelConfig } from 'types/models-config';
import { TridentCampaignDetails } from 'types/trident-campaigns';
import { queryStringFromObject } from 'utils/route-utils';
import TridentCampaignForm from 'views/trident-campaigns/common/TridentCampaignForm';
import TridentHomePage from 'views/trident-campaigns/index/TridentHomePage';
import TridentCampaignSearchForm from 'views/trident-campaigns/index/TridentCampaignSearchForm';
import TridentCampaignMetricsPage from 'views/trident-campaigns/metrics/TridentCampaignMetricsPage';
import TridentCampaignReviewPage from 'views/trident-campaigns/review/TridentCampaignReviewPage';
import {
  getActionAwardNFTConstraints,
  getActionAwardSubscriptionConstraints,
  getActionSendGrabChatConstraints,
  getActionUpdateSegmentMembershipConstraints,
  getAwardPointConstraints,
  getAwardRewardConstraints,
  getNewActionSendPushConstraints,
  getOptInChallengeConstraints,
  getUpdateQuestConstraints,
} from '../../components/form/trident-campaigns/constraints';


const {
  localeSelector,
  permissionKey,
  webPath,
  hasFieldsAccessFunc,
} = tridentLinkData;

const conditionToScenarioAttr = {
  'var.ride.pickupCityID': 'pickupCities',
  'var.ride.serviceID': 'taxiTypeIDs',
  'var.payment.currency': 'currency',
};

const SEGMENT_SUB_ACTIONS = ['updateSegmentMembership', 'updatePaxDigibankSegmentMembership', 'updateMexGroupMembership'];
const HOLOS_PROJECT_LINKING_CUTOFF = '2023-02-21';

export const DATE_FORMAT = 'DD/MM/YYYY hh:mm a';

// code to check whether treatment is ending with action or not
export const flowCompletedAndLimitFilled = function(object: any) {
  if (_.isEmpty(object.nodes)) {
    return 'should have at least one treatment';
  }

  const treatmentStartKeysList = [];
  const treatmentScenarioKeysList = [];
  const nodeKeys = _.keysIn(object.nodes);
  let randomizerVerifyArray = [];
  _.map(nodeKeys, (nodeKey) => {
    const nodeType = _.get(object.nodes[nodeKey], 'type', '');
    if (nodeType === nodeTypes.scenarios) {
      treatmentStartKeysList.push(nodeKey);
      treatmentScenarioKeysList.push(nodeKey);
    }
    if (nodeType === nodeTypes.time) {
      treatmentStartKeysList.push(nodeKey);
    }
    if (nodeType === nodeTypes.randomizer) {
      const validateArr = verifySubactionsForRandomizer(nodeKey, object);
      randomizerVerifyArray = [...randomizerVerifyArray, ...validateArr];
      randomizerVerifyArray = _.uniq(randomizerVerifyArray);
    }
  });
  const limitThere = limitPresenceCheck(treatmentScenarioKeysList, object);

  let flowCompletedBool = true;
  treatmentStartKeysList.forEach((treatmentStartKey) => {
    flowCompletedBool = flowCompletedBool && preOrderTraversalFromCurrentNodeToItsChildren(treatmentStartKey, flowCompletedBool, object);
  });
  if (randomizerVerifyArray.length) {
    let randomizerErr = ' -> Randomizer Error: ';
    for (let index = 0; index < randomizerVerifyArray.length; index += 1) {
      const element = randomizerVerifyArray[index];
      if (index === 0) {
        randomizerErr += element;
      } else {
        randomizerErr = `${randomizerErr}, ${element}`;
      }
    }
    return randomizerErr;
  }
  if (!flowCompletedBool) {
    return ' -> all treatments should end with actions only';
  }
  if (!limitThere) {
    return ' -> define limits either on scenario node or on all it\'s children action nodes';
  }
};

function limitPresenceCheck(treatmentStartKeysList: Array<string>, model: any) {
  let limitPresence = true;
  treatmentStartKeysList.forEach((node) => {
    if (_.isEmpty(_.get(model.nodes[node], 'limits') || [])) {
      limitPresence = limitPresence && checkActionLimitPresence(node, model);
    }
  });
  return limitPresence;
}

function checkActionLimitPresence(currentNodeKey: string, model: any) {
  if (_.isEmpty(_.get(model.nodes[currentNodeKey], 'children') || []) && _.get(model.nodes[currentNodeKey], 'type') !== 'actions') {
    return false;
  }
  if (_.get(model.nodes[currentNodeKey], 'type') === 'actions' && _.isEmpty(_.get(model.nodes[currentNodeKey], 'limits') || [])) {
    return false;
  }
  for (let i = 0; i < (_.get(model.nodes[currentNodeKey], 'children') || []).length; i += 1) {
    const childArray = (_.get(model.nodes[currentNodeKey], 'children') || []);
    return checkActionLimitPresence(childArray[i], model);
  }
  return true;
}

function preOrderTraversalFromCurrentNodeToItsChildren(currentNodeKey: string, flowCompletedBool: any, model: any) {
  if ((_.get(model.nodes[currentNodeKey], 'children') || []).length <= 0
    && (model.nodes[currentNodeKey].type !== 'actions')
    && !(model.nodes[currentNodeKey].type === 'experimentTreatment' && model.nodes[currentNodeKey].data.groupID === 0)
    && !_.get(model.nodes[currentNodeKey], 'recordLastOccurrenceKey')) {
    return false;
  }

  for (let i = 0; i < (_.get(model.nodes[currentNodeKey], 'children') || []).length; i += 1) {
    const childArray = (_.get(model.nodes[currentNodeKey], 'children') || []);
    flowCompletedBool = flowCompletedBool && preOrderTraversalFromCurrentNodeToItsChildren(childArray[i], flowCompletedBool, model);
  }
  return flowCompletedBool;
}

function verifySubactionsForRandomizer(currentNodeKey: string, model: any) {
  const childArray = (_.get(model.nodes[currentNodeKey], 'children') || []);
  let constraintObject = {};

  const verifySubactionItem = (actionNodeKey) => {
    return (element, subActionIndex) => {
      let localConstraints = {};
      switch (element.type) {
        case 'sendInbox2':
        case 'sendEmail':
        case 'pushChainedInbox':
        case 'sendPush':
        case 'sendSMS':
          localConstraints = getNewActionSendPushConstraints(actionNodeKey, subActionIndex);
          break;
        case 'awardReward':
          localConstraints = getAwardRewardConstraints(actionNodeKey, subActionIndex);
          break;
        case 'updateQuest':
          localConstraints = getUpdateQuestConstraints(actionNodeKey, subActionIndex);
          break;
        case 'sendSlack':
        case 'awardSubscription':
          localConstraints = getActionAwardSubscriptionConstraints(actionNodeKey, subActionIndex);
          break;
        case 'awardNFT':
          localConstraints = getActionAwardNFTConstraints(actionNodeKey, subActionIndex);
          break;
        case 'awardPoint':
          localConstraints = getAwardPointConstraints(actionNodeKey, subActionIndex);
          break;
        case 'updateSegmentMembership':
        case 'updateDaxGroupMembership':
          localConstraints = getActionUpdateSegmentMembershipConstraints(actionNodeKey, subActionIndex);
          break;
        case 'sendGrabChat':
          localConstraints = getActionSendGrabChatConstraints(actionNodeKey, subActionIndex);
          break;
        case 'optInChallenge':
          localConstraints = getOptInChallengeConstraints(actionNodeKey, subActionIndex);
          break;
        default:
          break;
      }
      constraintObject = Object.assign(constraintObject, localConstraints);
    };
  };

  for (let index = 0; index < childArray.length; index += 1) {
    const actionNodeKey = childArray[index];
    const actionNode = model.nodes[actionNodeKey];
    const subActions = _.get(actionNode, 'data') || [];

    subActions.forEach(verifySubactionItem(actionNodeKey));
  }
  const errorValidate = customValidate(model, constraintObject, false) || {};
  const arrayValues = Object.values(errorValidate);
  const returnValue = [];
  for (let index = 0; index < arrayValues.length; index += 1) {
    const element = arrayValues[index][0];
    returnValue.push(element.split('.meta ')[1]);
  }
  return _.uniq(returnValue);
}


// scenarioGetTransform returns transformed scenario node
// it checks whether the scenario has any special conditions, and puts those special conditions into node data attributes, and removes them from the condition object
function scenarioDataGetTransform(data: any) {
  _.get(data, 'scenarios', []).forEach((scenario, scenarioIndex) => {
    // case 1: scenario condition is a single condition
    if (!_.isEmpty(_.get(scenario.condition, 'lhs'))) {
      const lhs = _.get(scenario.condition, 'lhs');
      const attr = _.get(conditionToScenarioAttr, lhs);
      if (!_.isEmpty(attr)) {
        _.set(data, `scenarios[${scenarioIndex}].${attr}`, _.get(scenario.condition, 'rhs'));
        _.set(data, `scenarios[${scenarioIndex}].condition`, null);
      }
      return;
    }

    // case 2: scenario condition is a list of conditions
    const conditionsList = []; // list of non-special conditions that we need to retain in the condition object
    _.get(scenario.condition, 'conditions', []).forEach((condition) => {
      const attr = _.get(conditionToScenarioAttr, condition.lhs);
      if (!_.isEmpty(attr)) {
        _.set(data, `scenarios[${scenarioIndex}].${attr}`, condition.rhs);
      } else {
        conditionsList.push(condition);
      }
    });

    if (scenario.condition != null && !_.isEmpty(_.get(scenario.condition, 'conditions', []))) {
      if (_.isEmpty(conditionsList)) {
        _.set(data, `scenarios[${scenarioIndex}].condition`, null);
      } else if (conditionsList.length === 1) {
        _.set(data, `scenarios[${scenarioIndex}].condition`, conditionsList[0]);
      } else {
        _.set(data, `scenarios[${scenarioIndex}].condition.conditions`, conditionsList);
      }
    }
  });

  return data;
}

// model transform for get request
// it will modify that newModel that's passed in
function modelGetTransform(newModel: any) {
  _.set(newModel, 'nodes', JSON.parse(newModel.nodes));
  let recordLastOccurrenceKey = 0;

  _.map(newModel.nodes, (nodeData) => {
    if (nodeData.type === 'condition' || nodeData.type === 'scenarios') {
      if (_.get(nodeData, 'recordLastOccurrenceKey')) {
        let num = nodeData.recordLastOccurrenceKey.match(/\d/g).join('');
        num = parseInt(num, 10);
        if (num > recordLastOccurrenceKey) {
          recordLastOccurrenceKey = num;
        }
      }
    }
  });
  let maxValueOfNodesKeyFromApi = 0;
  const nodeKeys = _.keysIn(newModel.nodes);
  _.map(nodeKeys, (nodeKey) => {
    if (maxValueOfNodesKeyFromApi < parseInt(nodeKey, 10)) {
      maxValueOfNodesKeyFromApi = parseInt(nodeKey, 10);
    }
  });
  _.set(newModel, 'maxValueOfNodesKeyFromApi', maxValueOfNodesKeyFromApi);
  _.set(newModel, 'recordLastOccurrenceKey', recordLastOccurrenceKey);

  const nodesOccMap = nodesOccMapData;

  _.map(newModel.nodes, (node, index) => {
    if (node.limits === null) {
      _.unset(newModel.nodes[index], 'limits');
    }
    if (node.children === null) {
      _.unset(newModel.nodes[index], 'children');
    }
    const dynamicNodeName = {
      nodeID: index,
      key: nodesOccMap[node.type] ? (nodesOccMap[node.type].length + 1) : node.type,
    };
    if (!nodesOccMap[node.type]) {
      nodesOccMap[node.type] = [];
    }
    nodesOccMap[node.type].push(dynamicNodeName);
    if (node.type === 'scenarios') {
      _.set(node, 'data', scenarioDataGetTransform(node.data));
    }
    if (node.type === 'actions') {
      _.get(node, 'data', []).forEach((subAction, subIndex) => {
        if (subAction.type === 'pushNotification' || subAction.type === 'sendInbox' || subAction.type === 'inboxAndPush') {
          const pushData = _.get(subAction, 'meta.notificationPayload.pushData', {});
          let pushDataList = [];
          if (_.size(pushData) > 0) {
            const pushKeys = _.keysIn(pushData);
            _.map(pushKeys, (pushKey) => {
              const currentData = {
                key: pushKey,
                value: pushData[pushKey],
              };
              pushDataList = _.concat(pushDataList, currentData);
            });
          }
          _.set(newModel.nodes[index], `data[${subIndex}].meta.notificationPayload.pushDataList`, pushDataList);
        }
        if (subAction.type === 'bulkAwardReward' || subAction.type === 'awardReward') {
          if (_.get(subAction, 'meta.expireAfterMinutes') > 0) {
            let delta = _.get(subAction, 'meta.expireAfterMinutes');
            // calculate (and subtract) whole days
            const days = Math.floor(delta / 1440);
            const expireAfterMinutesDay = days;
            delta -= days * 1440;
            // calculate (and subtract) whole hours
            const hours = Math.floor(delta / 60);
            const expireAfterMinutesHour = hours;
            delta -= hours * 60;
            // calculate (and subtract) whole minutes
            const expireAfterMinutesMinute = delta;
            _.set(newModel.nodes[index], `data[${subIndex}].meta.expireAfterMinutesDay`, expireAfterMinutesDay);
            _.set(newModel.nodes[index], `data[${subIndex}].meta.expireAfterMinutesHour`, expireAfterMinutesHour);
            _.set(newModel.nodes[index], `data[${subIndex}].meta.expireAfterMinutesMinute`, expireAfterMinutesMinute);
          }
          _.set(newModel.nodes[index], `data[${subIndex}].isValid`, true);
        }
        if (subAction.type === 'updateSegmentMembership') {
          if (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutes') > 0) {
            let delta = _.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutes');
            // calculate (and subtract) whole days
            const days = Math.floor(delta / 1440);
            const expireUserAfterMinutesDay = days;
            delta -= days * 1440;
            // calculate (and subtract) whole hours
            const hours = Math.floor(delta / 60);
            const expireUserAfterMinutesHour = hours;
            delta -= hours * 60;
            // calculate (and subtract) whole minutes
            const expireUserAfterMinutesMinute = delta;
            _.set(newModel.nodes[index], `data[${subIndex}].meta.updateSegMembershipPayload.expireUserAfterMinutesDay`, expireUserAfterMinutesDay);
            _.set(newModel.nodes[index], `data[${subIndex}].meta.updateSegMembershipPayload.expireUserAfterMinutesHour`, expireUserAfterMinutesHour);
            _.set(newModel.nodes[index], `data[${subIndex}].meta.updateSegMembershipPayload.expireUserAfterMinutesMinute`, expireUserAfterMinutesMinute);
          }
        }
      });
    }
  });
  _.set(newModel, 'nodesOccMap', nodesOccMap);
}

const modelConfig: ModelConfig = {
  modelName: 'tridentCampaigns',
  localeSelector,
  permissionKey,
  hasDetailsHeader: true,
  stateKey: 'tridentCampaigns',
  webPath,
  apiPath: '/campaigns',
  allowCreation: true,
  allowDuplication: true,
  hasCountryIDPath: true,
  allowActivation: true,
  isStayOnCurrentPage: true,
  editErrorLocaleKey: 'edit',
  isContinuationToken: true,
  allowUpdateFunc: (model) => {
    return model.status !== CampaignStatus.active && model.status !== CampaignStatus.pending;
  },
  hasFieldUpdatePermissionFunc: (model, userPermissions) => hasTridentPermission(userPermissions, _.get(model, 'targetUserType', ''), _.get(model, 'owner', ''), 'update'),
  hasFieldsAccessFunc,
  hasDuplicatePath: (model) => {
    return `/campaigns/new?${queryStringFromObject({ entityIDToDuplicate: _.get(model, 'campaignID'), countryID: _.get(model, 'countryID') })}`;
  },
  modifyModelToDuplicateFunc: (model) => {
    const newModel = {
      ..._.cloneDeep(model),
      campaignID: '',
      status: 'DRAFT',
      name: '',
    };
    const nodeKeys = _.keysIn(_.get(newModel, 'nodes', {}));
    nodeKeys.forEach(nodeKey => {
      const node = _.get(newModel, `nodes.${nodeKey}`);

      if (node.type === nodeTypes.time || node.type === nodeTypes.schedule) {
        _.unset(node, 'data.nextTriggerTime');
        _.unset(node, 'data.lastHappenedAt');
        _.set(newModel, `nodes.${nodeKey}`, node);
      } else if (node.type === 'actions' && _.isArray(node.data)) {
        node.data.forEach((subAction, subIndex) => {
          if (subAction.type === 'awardReward' || subAction.type === 'bulkAwardReward') {
            _.set(newModel, `nodes[${nodeKey}].data[${subIndex}].isValid`, false);
          }
        });
      }
    });
    return newModel;
  },
  hasFieldCreatePermissionFunc: (model, userPermissions) => hasTridentPermission(userPermissions, _.get(model, 'targetUserType', ''), _.get(model, 'owner', ''), 'create'),

  actions: {
    create: createTarget,
    edit: editTarget,
    list: getTargets,
    fetch: getTarget,
  },
  payloadKey: {
    list: 'campaigns',
  },
  views: {
    index: TridentHomePage,
    new: NewPage,
    edit: EditPage,
    review: TridentCampaignReviewPage,
    metrics: TridentCampaignMetricsPage,
    search: TridentHomePage,
    entityForm: TridentCampaignForm,
    searchForm: TridentCampaignSearchForm,
  },
  routes: {
    index: '/trident',
    search: '/campaigns',
    edit: '/campaigns/:countryID',
    review: '/campaigns/:countryID',
    metrics: '/campaigns/:countryID',
  },
  idField: 'campaignID',
  getDefaultSearchParams: () => ({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  }),
  nameField: 'name',
  defaultEntity: () => ({
    countryID: 0,
    name: '',
    status: 'DRAFT',
    description: '',
    startTime: '',
    endTime: '',
    updatedAt: '',
    timezone: '',
    timezoneName: '',
    nodes: {},
    nodesOccMap: nodesOccMapData,
    recordLastOccurrenceKey: 0,
  }),
  constraints: {
    name: {
      presence: true,
    },
    status: {
      presence: true,
    },
    countryID: {
      presence: true,
      numericality: { greaterThanOrEqualTo: 1 },
    },
    targetUserType: {
      presence: true,
    },
    startTime: {
      presence: true,
      dateRange: { start: 'startTime', end: 'endTime', format: DATE_FORMAT },
    },
    endTime: {
      presence: true,
      dateRange: { start: 'startTime', end: 'endTime', format: DATE_FORMAT },
    },
    nodes: {
      validateByFunc: { validationFunc: flowCompletedAndLimitFilled },
    },
    metadata: {
      validateByFunc: {
        validationFunc: (model: TridentCampaignDetails) => {
          const campaignMetadataFields = _.get(window.Grab.config, 'CAMPAIGN_METADATA_FIELDS', []);
          for (const field of campaignMetadataFields) {
            if (field.isRequired && _.isEmpty(_.get(model, `metadata.${field.payloadPath}`))) {
              return `${field.label} is mandatory`;
            }
          }
        },
      },
    },
    holosProject: {
      validateByFunc: {
        validationFunc: (model: TridentCampaignDetails) => {
          const project = _.get(model, 'ucdProject.project');
          const targetUserType = _.get(model, 'targetUserType');
          const owner = _.get(model, 'owner');
          let isCreatedBeforeCutoff = false;
          const createdAt = _.get(model, 'createdAt');
          if (createdAt !== null) {
            isCreatedBeforeCutoff = (moment(createdAt) < moment(HOLOS_PROJECT_LINKING_CUTOFF));
          }

          if (!project && targetUserType === 'passenger' && owner === 'TARGETING' && !isCreatedBeforeCutoff) {
            return ' : Please note that it is now mandatory to link your PAX targeting campaign to a Holos Project. https://wiki.grab.com/display/MKT/3.1.+Trident';
          }
          if (project) {
            if (_.get(project, 'countryID') !== _.get(model, 'countryID')) {
              return 'does not match the campaign\'s country';
            }
            if (tridentUserVsUCDUser[_.get(model, 'targetUserType')] !== _.get(project, 'audience')) {
              return 'does not match the campaign\'s user type';
            }
            const campaignStartDateTime = _.get(model, 'startTime');
            const campaignEndDateTime = _.get(model, 'endTime');
            if (!(campaignStartDateTime && campaignEndDateTime)) {
              return 'choose startTime or endTime';
            }
          } else {
            // TODO: Uncomment later, disabled temporarily until 26 Jan (at least)
            // if ((_.get(model, 'targetUserType', '') === targetUserTypes.pax)  && (_.get(model, 'owner', '') === ownerTypes.targeting)) {
            //   const nodes = _.get(model, 'nodes', {});
            //   if (nodesContainCommsAction(nodes)) {
            //     return 'must be selected for PAX campaign that has comm action';
            //   }
            // }
          }
        },
      },
    },
    budgetSource: {
      validateByFunc: {
        validationFunc: (model: Object) => {
          if (model.ucdProjectID && !model.budgetSource) {
            return 'required when UCDProjectID is chosen';
          }
        },
      },
    },
    targetVertical: {
      validateByFunc: {
        validationFunc: (model: Object) => {
          if (model.ucdProjectID && !model.targetVertical) {
            return 'required when UCDProjectID is chosen';
          }
        },
      },
    },
    targetVerticalCVP: {
      validateByFunc: {
        validationFunc: (model: Object) => {
          if (model.ucdProjectID && !model.targetVerticalCVP) {
            return 'required when UCDProjectID is chosen';
          }
        },
      },
    },
  },
  viewBuilderParams: {
    momentFields: ['startTime', 'endTime'],
    countryIDField: 'countryID',
    payloadFetchTransform: (model) => {
      const newModel = _.cloneDeep(model);
      newModel.campaignID = newModel.campaignID ? newModel.campaignID : 'dummyID'; // if not getting id from backend, set here a dummy id.

      // for Get request, backend will return nodes as a string, and we need to transform it for proper display
      // for List request, backend will either not return the nodes or return nodes as an object, and there is no need to do transform for that case
      if (_.get(newModel, 'nodes') && _.isString(newModel.nodes)) {
        modelGetTransform(newModel);
      }

      return newModel;
    },
    payloadSendTransform: (model) => {
      const newModel = _.cloneDeep(model);
      delete newModel.maxValueOfNodesKeyFromApi;
      delete newModel.recordLastOccurrenceKey;
      _.map(newModel.nodes, (node, index) => {
        if (node.type === nodeTypes.scenarios) {
          _.get(node, 'data.scenarios', []).forEach((scenario, scenarioIndex) => {
            const conditionsList = [];
            if (!_.isEmpty(_.get(scenario, 'pickupCities')) && !_.get(scenario, 'allCitiesSelected', false)) {
              // NOTE: if all cities are selected, that means we don't have condition for city
              const rule = {
                lhs: 'var.ride.pickupCityID',
                operator: 'in',
                rhs: scenario.pickupCities,
              };
              conditionsList.push(rule);
            }
            if (!_.isEmpty(_.get(scenario, 'taxiTypeIDs')) && !_.get(scenario, 'allTaxiTypesSelected', false)) {
              // NOTE: if all taxi types are selected, that means we don't have condition for taxi type
              const rule = {
                lhs: 'var.ride.serviceID',
                operator: 'in',
                rhs: scenario.taxiTypeIDs,
              };
              conditionsList.push(rule);
            }
            if (!_.isEmpty(_.get(scenario, 'currency'))) {
              const rule = {
                lhs: 'var.payment.currency',
                operator: 'eq',
                rhs: scenario.currency,
              };
              conditionsList.push(rule);
            }

            if (!_.isEmpty(conditionsList)) {
              if (scenario.condition != null) {
                conditionsList.push(scenario.condition);
              }

              const scenarioCond = conditionsList.length > 1 ? { operator: 'and', conditions: conditionsList } : conditionsList[0];
              _.set(newModel.nodes[index], `data.scenarios[${scenarioIndex}].condition`, scenarioCond);
            }
          });
        }
        if (node.type === nodeTypes.actions) {
          _.get(node, 'data', []).forEach((subAction, subIndex) => {
            if (subAction.type === 'pushNotification' || subAction.type === 'sendInbox' || subAction.type === 'inboxAndPush') {
              const pushData = {};
              _.map(subAction.meta.notificationPayload.pushDataList, (pushVal) => {
                _.set(pushData, pushVal.key, pushVal.value);
              });
              _.set(newModel.nodes[index], `data[${subIndex}].meta.notificationPayload.pushData`, pushData);
            }
            if (subAction.type === 'sendSlack') {
              const data = _.get(subAction, 'meta.slackPayload.data', '');
              const channelsWithWhitespace = data.split(',');
              const channels = [];
              for (let i = 0; i < channelsWithWhitespace.length; i += 1) {
                channels[i] = channelsWithWhitespace[i].trim();
              }
              const template = _.get(subAction.meta, 'slackPayload.template', '');
              _.set(newModel.nodes[index], `data[${subIndex}].meta.slackPayload.channels`, channels);
              _.set(newModel.nodes[index], `data[${subIndex}].meta.slackPayload.template`, template);
            }
            if (subAction.type === 'bulkAwardReward' || subAction.type === 'awardReward') {
              let minutes = 0;
              if (_.get(subAction, 'meta.expireAfterMinutesDay') > 0) {
                minutes += (_.get(subAction, 'meta.expireAfterMinutesDay') * 1440);
              }
              if (_.get(subAction, 'meta.expireAfterMinutesHour') > 0) {
                minutes += (_.get(subAction, 'meta.expireAfterMinutesHour') * 60);
              }
              if (_.get(subAction, 'meta.expireAfterMinutesMinute') > 0) {
                minutes += (_.get(subAction, 'meta.expireAfterMinutesMinute'));
              }
              _.set(newModel.nodes[index], `data[${subIndex}].meta.expireAfterMinutes`, minutes);
            }
            if (subAction.type === 'updateSegmentMembership' || subAction.type === 'updateDaxGroupMembership') {
              let minutes = 0;
              if (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesDay') > 0) {
                minutes += (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesDay') * 1440);
              }
              if (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesHour') > 0) {
                minutes += (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesHour') * 60);
              }
              if (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesMinute') > 0) {
                minutes += (_.get(subAction, 'meta.updateSegMembershipPayload.expireUserAfterMinutesMinute'));
              }
              _.set(newModel.nodes[index], `data[${subIndex}].meta.updateSegMembershipPayload.expireUserAfterMinutes`, minutes);
            }
            if (subAction.type === 'awardPoint') {
              const fields = ['costCenter', 'fiatCurrency', 'product', 'vertical', 'pricePerPoint'];
              const values = _.map(fields, f => { return _.get(subAction, `meta.financeData.${f}`); });

              if (_.every(values, val => (!!val))) {
                _.set(newModel.nodes[index], `data[${subIndex}].meta.financeData.isFinanceDataAvailable`, true);
              } else {
                _.set(newModel.nodes[index], `data[${subIndex}].meta.financeData.isFinanceDataAvailable`, false);
              }
            }
          });
        }
        if (node.type === nodeTypes.time || node.type === nodeTypes.schedule) {
          if (!_.get(newModel.nodes[index], 'data.nextTriggerTime')) _.unset(newModel.nodes[index], 'data.nextTriggerTime');
          if (!_.get(newModel.nodes[index], 'data.lastHappenedAt')) _.unset(newModel.nodes[index], 'data.lastHappenedAt');
        }
      });
      return newModel;
    },
    getViewBuilderParams: (model) => {
      const viewBuilderParams = {};
      const momentFields = [];
      const numberFields = [];
      momentFields.push('updatedAt');
      _.map(model.nodes, (node, index) => {
        if (node.type === nodeTypes.time || node.type === nodeTypes.schedule) {
          momentFields.push(`nodes.${index}.data.startTime`);
          momentFields.push(`nodes.${index}.data.endTime`);
          momentFields.push(`nodes.${index}.data.nextTriggerTime`);
          momentFields.push(`nodes.${index}.data.lastHappenedAt`);
        }
        if (node.type === nodeTypes.scenarios) {
          momentFields.push(`nodes.${index}.data.meta.startTime`);
          momentFields.push(`nodes.${index}.data.meta.endTime`);
        }
        if (node.type === nodeTypes.actions && !_.isNil(node.data)) {
          _.get(node, 'data', []).forEach((subAction, subIndex) => {
            if (SEGMENT_SUB_ACTIONS.includes(subAction.type)) {
              if (_.get(subAction, 'meta.updateSegMembershipPayload.segmentExpiryDate')) {
                momentFields.push(`nodes.${index}.data[${subIndex}].meta.updateSegMembershipPayload.segmentExpiryDate`);
              }
            }
          });
        }
      });
      _.set(viewBuilderParams, 'momentFields', momentFields);
      _.map(model.nodes, (node, index) => {
        if (node.type === nodeTypes.time) {
          if (_.get(node, 'data.interval')) {
            numberFields.push(`nodes.${index}.data.interval`);
          }
        }
        if (node.type === nodeTypes.scenarios) {
          for (let i = 0; i < (_.get(node, 'limits') || []).length; i += 1) {
            numberFields.push(`nodes.${index}.limits[${i}].limit`);
          }
        }
        if (node.type === nodeTypes.actions) {
          for (let i = 0; i < (_.get(node, 'limits') || []).length; i += 1) {
            numberFields.push(`nodes.${index}.limits[${i}].limit`);
          }
        }
      });
      _.set(viewBuilderParams, 'numberFields', numberFields);
      return viewBuilderParams;
    },
  },
};

_.set(modelConfig, 'tableColumns', [
  {
    key: 'name.keyword',
    name: 'Campaign Code',
    renderFunc: (entity: Object) => (
      <Link to={`${_.get(modelConfig, 'webPath')}/${_.get(entity, 'countryID')}/${_.get(entity, modelConfig.idField)}/edit`}>
        {entity.name}
      </Link>),
  },
  {
    key: 'status',
    renderFunc: (entity: Object) => (
      <span>
        <StatusLabel status={entity.status} />
      </span>),
  },
  {
    key: 'countryID',
    name: 'Country',
    renderFunc: (entity: Object) => (
      countries[entity.countryID]),
  },
  {
    key: 'startTime',
    renderFunc: (entity: Object) => (
      <Popup
        trigger={
          <span>
            {moment(entity.startTime).format('MM/DD/YYYY')}
          </span>
        }
        content={moment(entity.startTime).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
  {
    key: 'endTime',
    renderFunc: (entity: Object) => (
      <Popup
        trigger={
          <span>
            {moment(entity.endTime).format('MM/DD/YYYY')}
          </span>
        }
        content={moment(entity.endTime).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
  {
    key: 'updatedAt',
    name: 'Last Updated',
    renderFunc: (entity: Object) => (
      <Popup
        trigger={
          <span>
            {moment(entity.updatedAt).fromNow()}
          </span>
        }
        content={moment(entity.updatedAt).format('MMMM Do YYYY, h:mm:ss a')}
      />
    ),
  },
]);

export default modelConfig;
